"use client";

import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, ArrowRight, Clock, TrendingUp } from "lucide-react";
import { client } from "@/sanity/lib/client";
import Link from "next/link";
import Image from "next/image";
import { urlForImage } from "@/sanity/lib/image";

interface SearchResult {
  _id: string;
  title: string;
  slug: { current: string };
  mainImage: any;
  publishedAt: string;
  categories: { title: string }[];
}

export default function SearchOverlay({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Simulation of "Trending" data - in real app, fetch from Sanity
  const trending = ["IPL 2026", "Global Economy", "Silicon Valley AI", "Climate Shift"];

  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setIsSearching(true);
      try {
        const searchQuery = `*[_type == "post" && (title match $term || body[].children[].text match $term)] | order(publishedAt desc)[0...5] {
          _id, title, slug, mainImage, publishedAt, categories[]->{title}
        }`;
        const data = await client.fetch(searchQuery, { term: `${query}*` });
        setResults(data);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {children}
      </Dialog.Trigger>

      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-xl"
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="fixed inset-x-0 top-0 z-[101] w-full bg-background border-b border-border shadow-2xl max-h-screen overflow-y-auto"
              >
                <Dialog.Title className="sr-only">Global Search Engine</Dialog.Title>
                <div className="container mx-auto px-4 md:px-8 py-8 md:py-12 max-w-[1000px]">

                  {/* Search Input Area */}
                  <div className="relative flex items-center mb-8 md:mb-12">
                    <Search className="absolute left-0 w-8 h-8 text-brand" />
                    <input
                      autoFocus
                      className="w-full bg-transparent border-0 border-b-2 border-border focus:border-brand transition-colors text-3xl md:text-5xl font-serif font-bold pl-12 pb-4 outline-none placeholder:text-muted-foreground/20"
                      placeholder="Search World Prime..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                    <Dialog.Close asChild>
                      <button className="absolute right-0 p-2 hover:bg-muted rounded-full transition-colors">
                        <X className="w-8 h-8" />
                      </button>
                    </Dialog.Close>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-12">

                    {/* LEFT: Live Results */}
                    <div className="md:col-span-8">
                      {isSearching ? (
                        <div className="space-y-6">
                          {[1, 2, 3].map(i => (
                            <div key={i} className="flex gap-4 animate-pulse">
                              <div className="w-24 h-16 bg-muted rounded" />
                              <div className="flex-1 space-y-2">
                                <div className="h-4 bg-muted w-3/4 rounded" />
                                <div className="h-3 bg-muted w-1/4 rounded" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : results.length > 0 ? (
                        <div className="space-y-8">
                          <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-brand border-b border-brand/20 pb-2">Editorial Matches</h3>
                          {results.map((post) => (
                            <Link
                              key={post._id}
                              href={`/post/${post.slug.current}`}
                              onClick={() => setOpen(false)}
                              className="group flex gap-6 items-start hover:bg-muted/30 p-2 -m-2 rounded-sm transition-all"
                            >
                              {post.mainImage && (
                                <div className="relative w-32 aspect-[1.4/1] overflow-hidden rounded-sm shrink-0">
                                  <Image
                                    src={urlForImage(post.mainImage).width(300).url()}
                                    alt={post.title}
                                    fill
                                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="text-[9px] font-bold text-brand uppercase tracking-widest mb-1">
                                  {post.categories?.[0]?.title || "Editorial"}
                                </div>
                                <h4 className="font-serif text-xl font-bold leading-tight group-hover:text-brand transition-colors line-clamp-2">
                                  {post.title}
                                </h4>
                              </div>
                            </Link>
                          ))}
                          {/* Removed "View all results" link as it led to a redundant search page */}
                        </div>
                      ) : query.length > 0 && !isSearching ? (
                        <div className="py-12 text-center border-2 border-dashed border-border rounded-lg">
                          <p className="text-muted-foreground italic">No matches found for "{query}"</p>
                        </div>
                      ) : (
                        <div className="space-y-8">
                          <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-muted-foreground/40 border-b border-border pb-2">Direct Access</h3>
                          <div className="grid grid-cols-2 gap-4">
                            {["World News", "Deep Analysis", "Markets", "Technology"].map(item => (
                              <button key={item} className="flex items-center justify-between p-4 bg-muted/50 hover:bg-brand hover:text-white transition-all rounded-sm text-left group">
                                <span className="font-bold text-sm">{item}</span>
                                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* RIGHT: Trends & Recents */}
                    <div className="md:col-span-4 space-y-12">
                      {/* Trending */}
                      <div>
                        <h3 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-brand mb-4">
                          <TrendingUp className="w-3 h-3" /> Trending Now
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {trending.map(t => (
                            <button
                              key={t}
                              onClick={() => setQuery(t)}
                              className="px-3 py-1.5 bg-muted hover:bg-brand/10 hover:text-brand text-[10px] font-bold border border-border transition-colors rounded-sm"
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Recent Searches Simulation */}
                      <div>
                        <h3 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-muted-foreground/40 mb-4">
                          <Clock className="w-3 h-3" /> Recent Activity
                        </h3>
                        <div className="space-y-4">
                          {["Nifty 50 Live", "AI Regulations", "Global Oil Prices"].map(r => (
                            <button key={r} onClick={() => setQuery(r)} className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors w-full text-left">
                              <Search className="w-3 h-3 opacity-30" />
                              <span>{r}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                  </div>

                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
